import * as THREE from 'three';
import { hexToWorld, type BattleState, type MapDef } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import type { AscuasSky } from './ascuas.ts';
import { WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';
import type { TerrainBuild } from './terrain.ts';

/**
 * The runaway carts (Las vías 1, docs/SAGAS.md): the clearing re-dressed as the station of Hilde's clan deep under the
 * shell. Slate floor and stone spires instead of the woods, a dark, cool murk, dwarven lamps on posts round the
 * station and in the timbered tunnel mouths, and the Marrow's red far below the cavern's rim. The rail the train runs
 * on (sleepers and two iron rails, round the cavern and out); the train itself, a few riveted mine carts with Hilde in
 * the first; a cart that leaves the rails tumbles off and lies there in pieces. At the second road's mouth, a chasm and
 * the drawbridge over it (raised once its counterweight drops, down again when that is broken); the counterweight
 * hangs from a timber frame at the far end of that road, its chain running back along the road to the bridge.
 *
 * Draw calls: the station (one merged mesh: rails, timbers, lamps' posts, spires, chasm lips, the frame), the lamps'
 * glass (one), the chasm's dark (one), the bridge leaf, the counterweight, the carts (one instanced mesh), the
 * wrecks (one instanced mesh), a glow layer. No lights.
 */

type Pt = { x: number; y: number };

const WOOD = ['#4a372b', '#3d2e25', '#55402f', '#33271f'];
const SLEEPER = ['#3a2c23', '#2f241d', '#44342a'];
const IRON = '#5c6068';
const IRON_DARK = '#35373c';
const ROCK = ['#3a3a40', '#46454c', '#2e2e33', '#52505a'];
const LAMP = new THREE.Color('#ffa66b');
const LAMP_CORE = new THREE.Color('#ffe0c4');
const MARROW = new THREE.Color('#ff3a1a');
const SPARK = new THREE.Color('#ffb36b');
/** A cart's spacing along the rail (sim units) and how many can be drawn. */
const GAP = 720;
const MAX_CARTS = 8;
const LEAF_UP = 1.2;

const lum = (r: number, g: number, b: number): number => 0.2126 * r + 0.7152 * g + 0.0722 * b;
const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

/** Repaints a vertex-coloured mesh between two colours, keeping its old light and shade. */
const repaint = (geo: THREE.BufferGeometry, dark: THREE.Color, light: THREE.Color, lo: number, hi: number): void => {
  const col = geo.getAttribute('color') as THREE.BufferAttribute | undefined;
  if (!col) return;
  const c = new THREE.Color();
  for (let i = 0; i < col.count; i++) {
    c.copy(dark).lerp(light, clamp01((lum(col.getX(i), col.getY(i), col.getZ(i)) - lo) / (hi - lo)));
    col.setXYZ(i, c.r, c.g, c.b);
  }
  col.needsUpdate = true;
};

/** One mine cart, facing +z, its floor at y = 0.12: a riveted tub of iron-banded planks, low iron wheels, a load of ore. */
const cartGeo = (): THREE.BufferGeometry => {
  const kit = new Kit();
  const o = new THREE.Matrix4();
  const box = new THREE.BoxGeometry(1, 1, 1);
  // The tub: a square frustum (wider at the top), stretched along the rail.
  const tub = new THREE.CylinderGeometry(0.34, 0.27, 0.3, 4, 1).rotateY(Math.PI / 4);
  kit.add(tub, '#5a4130', o, place(0, 0.28, 0, 0, new THREE.Vector3(0.82, 1, 1.12)));
  // Plank seams: darker strips down the sides.
  for (const z of [-0.12, 0.12]) for (const sx of [-1, 1]) kit.add(box, '#3e2c21', o, place(sx * 0.255, 0.28, z, 0, new THREE.Vector3(0.012, 0.27, 0.02), 0, sx * -0.11));
  // Two iron bands round it and an iron lip on top.
  const band = new THREE.CylinderGeometry(0.335, 0.31, 0.045, 4, 1, true).rotateY(Math.PI / 4);
  for (const y of [0.2, 0.33]) kit.add(band, IRON_DARK, o, place(0, y, 0, 0, new THREE.Vector3(0.84, 1, 1.14)));
  kit.add(new THREE.CylinderGeometry(0.355, 0.345, 0.035, 4, 1, true).rotateY(Math.PI / 4), IRON, o, place(0, 0.43, 0, 0, new THREE.Vector3(0.84, 1, 1.14)));
  // Rivets along the bands.
  const rivet = new THREE.SphereGeometry(0.014, 4, 3);
  for (const y of [0.2, 0.33])
    for (const t of [-0.15, 0, 0.15]) {
      kit.add(rivet, '#8a8e96', o, place(0.268 + (0.33 - y) * 0.2, y, t));
      kit.add(rivet, '#8a8e96', o, place(-0.268 - (0.33 - y) * 0.2, y, t));
    }
  // Inside: the dark of the tub, ore heaped in it (a red-brown glint of iron in the grey).
  kit.add(box, '#17120f', o, place(0, 0.4, 0, 0, new THREE.Vector3(0.46, 0.02, 0.62)));
  const ore = new THREE.DodecahedronGeometry(0.08, 0);
  const lumps: [number, number, number, string][] = [
    [-0.1, 0.44, -0.16, '#45444a'],
    [0.08, 0.46, -0.1, '#55535a'],
    [-0.04, 0.47, 0.04, '#3b3a40'],
    [0.12, 0.43, 0.14, '#5a4038'],
    [-0.12, 0.44, 0.17, '#4c4b52'],
    [0.02, 0.5, -0.02, '#6a4434'],
  ];
  lumps.forEach(([x, y, z, c], i) => kit.add(ore, c, o, place(x, y, z, i * 1.3, 0.9 + (i % 3) * 0.25, i * 0.7)));
  // The chassis and its axles, four small iron wheels with hubs.
  kit.add(box, '#2a2626', o, place(0, 0.12, 0, 0, new THREE.Vector3(0.36, 0.06, 0.62)));
  const wheel = new THREE.CylinderGeometry(0.085, 0.085, 0.045, 10).rotateZ(Math.PI / 2);
  const hub = new THREE.CylinderGeometry(0.03, 0.03, 0.06, 6).rotateZ(Math.PI / 2);
  for (const z of [-0.2, 0.2])
    for (const sx of [-1, 1]) {
      kit.add(wheel, '#3a3c42', o, place(sx * 0.2, 0.085, z));
      kit.add(hub, '#6a6e76', o, place(sx * 0.22, 0.085, z));
    }
  // The couplings, front and back.
  for (const z of [-0.36, 0.36]) kit.add(box, IRON_DARK, o, place(0, 0.14, z, 0, new THREE.Vector3(0.05, 0.04, 0.12)));
  const mesh = kit.build(new THREE.MeshBasicMaterial(), 'mine-cart') as THREE.Mesh;
  (mesh.material as THREE.Material).dispose();
  for (const g of [box, tub, band, rivet, ore, wheel, hub]) g.dispose();
  return mesh.geometry;
};

export class MineLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow = new GlowLayer(160);
  private carts: THREE.InstancedMesh;
  private wrecks: THREE.InstancedMesh;
  private leaf: THREE.Mesh;
  private weight: THREE.Mesh;
  /** The counterweight's chain, from the pulley down to the block (a unit length of links, stretched). */
  private chain: THREE.Mesh;
  /** Each segment of the rail once (the laps go round the same ring), in world points. */
  private segs: { a: THREE.Vector3; b: THREE.Vector3 }[] = [];
  private lamps: THREE.Vector3[] = [];
  private rim: THREE.Vector3[] = [];
  /** The bridge: its hinge (far side), the way the rail runs over it (unit), how long it is; the chasm's middle. */
  private hinge = new THREE.Vector3();
  private along = new THREE.Vector3();
  private span = 0;
  private chasm = new THREE.Vector3();
  /** The counterweight's frame top (where its chain hangs from) and its foot on the ground. */
  private frameTop = new THREE.Vector3();
  private frameFoot = new THREE.Vector3();
  private leafAngle = 0;
  private weightY = 0;
  private now = 0;
  private last = 0;
  private s: BattleState | null = null;
  /** The carts that came off the rails: where, which way they rolled, when (frame clock). */
  private wrecked: { at: THREE.Vector3; side: THREE.Vector3; yaw: number; t0: number }[] = [];
  /** Over the edge: when the train went (frame clock), and the carts as they were then. */
  private fellAt = -1;
  private fellMats: THREE.Matrix4[] = [];
  private yawCache = new Map<number, number>();
  private m = new THREE.Matrix4();
  private q = new THREE.Quaternion();
  private e = new THREE.Euler();
  private v = new THREE.Vector3();
  private w = new THREE.Vector3();
  private one = new THREE.Vector3(1, 1, 1);
  private pts: Pt[] = [];
  /** The first cart as drawn this frame (Hilde rides in it), and its yaw. */
  readonly head = new THREE.Vector3();
  headYaw = 0;
  shown = 0;

  constructor(
    private ctx: OmenCtx,
    private map: MapDef,
    private road: Pt[],
    private bridge: number,
  ) {
    this.group.name = 'mine';
    const at = (p: Pt, h = 0): THREE.Vector3 => ctx.at(p.x, p.y, h);
    // The rail's segments, each once.
    const seen = new Set<string>();
    for (let i = 1; i < road.length; i++) {
      const a = road[i - 1] as Pt;
      const b = road[i] as Pt;
      const k = a.x < b.x || (a.x === b.x && a.y < b.y) ? `${a.x},${a.y},${b.x},${b.y}` : `${b.x},${b.y},${a.x},${a.y}`;
      if (seen.has(k)) continue;
      seen.add(k);
      this.segs.push({ a: at(a), b: at(b) });
    }
    // The bridge: over the rail's first stretch out of the clearing along the second road.
    const g0 = at(road[bridge] as Pt);
    const g1 = at((road[bridge + 1] ?? road[bridge]) as Pt);
    this.along.subVectors(g1, g0).setY(0).normalize();
    const segLen = g0.distanceTo(g1);
    this.span = Math.min(1.0, segLen * 0.75);
    this.chasm.copy(g0).lerp(g1, 0.5);
    this.chasm.y = ctx.ground(this.chasm.x, this.chasm.z);
    this.hinge.copy(this.chasm).addScaledVector(this.along, this.span / 2);

    const kit = new Kit();
    const glass = new Kit();
    const box = new THREE.BoxGeometry(1, 1, 1);
    const post = new THREE.CylinderGeometry(0.05, 0.06, 1, 6);
    const rock = new THREE.DodecahedronGeometry(0.12, 0);
    const wood = (i: number): string => WOOD[((i % WOOD.length) + WOOD.length) % WOOD.length] as string;
    const gapA = this.span / 2 + 0.05;

    // The rail: sleepers every so often, two iron rails on them (none over the chasm: the bridge carries its own).
    let n = 0;
    for (const sg of this.segs) {
      const d = this.w.subVectors(sg.b, sg.a).setY(0);
      const len = d.length();
      if (len < 1e-3) continue;
      const yaw = Math.atan2(d.x, d.z);
      const steps = Math.max(1, Math.round(len / 0.3));
      const overChasm = (p: THREE.Vector3): boolean => Math.abs(this.v.subVectors(p, this.chasm).dot(this.along)) < gapA && this.v.subVectors(p, this.chasm).setY(0).length() < 0.9;
      for (let i = 0; i < steps; i++) {
        const t = (i + 0.5) / steps;
        const p = sg.a.clone().lerp(sg.b, t);
        if (overChasm(p)) continue;
        const y = ctx.ground(p.x, p.z);
        kit.add(box, SLEEPER[n++ % SLEEPER.length] as string, place(p.x, y + 0.025, p.z, yaw + ((n * 7) % 5 - 2) * 0.03, new THREE.Vector3(0.62, 0.05, 0.12)));
      }
      // The rails, in short pieces so they follow the ground and stop at the chasm's lips.
      const pieces = Math.max(1, Math.round(len / 0.4));
      for (let i = 0; i < pieces; i++) {
        const p0 = sg.a.clone().lerp(sg.b, i / pieces);
        const p1 = sg.a.clone().lerp(sg.b, (i + 1) / pieces);
        const mid = p0.clone().lerp(p1, 0.5);
        if (overChasm(mid)) continue;
        const y = Math.max(ctx.ground(p0.x, p0.z), ctx.ground(mid.x, mid.z), ctx.ground(p1.x, p1.z)) + 0.075;
        const l = p0.distanceTo(p1) + 0.06;
        for (const side of [-1, 1]) {
          const ox = Math.cos(yaw) * side * 0.2;
          const oz = -Math.sin(yaw) * side * 0.2;
          kit.add(box, IRON, place(mid.x + ox, y, mid.z + oz, yaw, new THREE.Vector3(0.045, 0.05, l)));
          kit.add(box, IRON_DARK, place(mid.x + ox, y - 0.035, mid.z + oz, yaw, new THREE.Vector3(0.07, 0.02, l)));
        }
      }
    }

    // The chasm's lips: broken rock either side of the gap, across the road.
    const across = new THREE.Vector3(this.along.z, 0, -this.along.x);
    for (const side of [-1, 1])
      for (let i = 0; i < 7; i++) {
        const p = this.chasm.clone().addScaledVector(this.along, side * (gapA + 0.02)).addScaledVector(across, -0.9 + i * 0.3);
        kit.add(rock, ROCK[(i + (side > 0 ? 1 : 0)) % ROCK.length] as string, place(p.x, ctx.ground(p.x, p.z) + 0.02, p.z, i * 1.7, 0.9 + (i % 3) * 0.35, i * 0.6));
      }
    // The bridge's posts and its winch chains up to the far posts' tops.
    for (const side of [-1, 1]) {
      const p = this.hinge.clone().addScaledVector(this.along, 0.12).addScaledVector(across, side * 0.42);
      const y = ctx.ground(p.x, p.z);
      kit.add(post, wood(side + 2), place(p.x, y + 0.55, p.z, 0, new THREE.Vector3(1.4, 1.1, 1.4)));
      kit.add(box, IRON_DARK, place(p.x, y + 1.1, p.z, 0, new THREE.Vector3(0.12, 0.08, 0.12)));
      const q = this.chasm.clone().addScaledVector(this.along, -gapA - 0.12).addScaledVector(across, side * 0.42);
      const yq = ctx.ground(q.x, q.z);
      kit.add(post, wood(side + 3), place(q.x, yq + 0.35, q.z, 0, new THREE.Vector3(1.2, 0.7, 1.2)));
    }

    // Round the station: dwarven lamps on posts at the clearing's edge, clear of the roads' mouths.
    const gates = map.sectors.map((sec) => {
      const c = hexToWorld(sec.gate.q, sec.gate.r);
      return Math.atan2(c.y, c.x);
    });
    const R = (map.bastion + 0.55) * WORLD_PER_HEX * 0.9;
    for (let i = 0; i < 9; i++) {
      const a = (i / 9) * Math.PI * 2 + 0.2;
      if (gates.some((g) => Math.abs(Math.atan2(Math.sin(a - g), Math.cos(a - g))) < 0.34)) continue;
      const p = new THREE.Vector3(Math.cos(a) * R, 0, Math.sin(a) * R);
      p.y = ctx.ground(p.x, p.z);
      this.lampPost(kit, glass, p, a + Math.PI, i);
    }
    // The tunnels: timber frames over the roads near their mouths, a lamp hung in every other one.
    map.sectors.forEach((sec, si) => {
      const path = sec.path.slice().reverse();
      for (let i = 1; i < Math.min(5, path.length); i += 1) {
        const h0 = path[i] as { q: number; r: number };
        const h1 = (path[i - 1] ?? h0) as { q: number; r: number };
        const c0 = hexToWorld(h0.q, h0.r);
        const c1 = hexToWorld(h1.q, h1.r);
        const p = ctx.at(c0.x, c0.y, 0);
        const to = ctx.at(c1.x, c1.y, 0);
        const yaw = Math.atan2(to.x - p.x, to.z - p.z);
        // None over the chasm: the bridge stands there.
        if (Math.hypot(p.x - this.chasm.x, p.z - this.chasm.z) < 1.3) continue;
        this.timbers(kit, glass, p, yaw, si * 7 + i, i % 2 === 1);
      }
    });

    // The counterweight's frame at the far end of the second road: two posts, a beam, a pulley; its chain runs back
    // along the road's edge to the bridge.
    const end = at(road[road.length - 1] as Pt);
    const prev = at((road[road.length - 2] ?? road[road.length - 1]) as Pt);
    const fyaw = Math.atan2(end.x - prev.x, end.z - prev.z);
    const fm = place(end.x, ctx.ground(end.x, end.z), end.z, fyaw);
    for (const sx of [-1, 1]) {
      kit.add(post, wood(sx + 1), fm, place(sx * 0.62, 1.05, 0, 0, new THREE.Vector3(2.2, 2.1, 2.2)));
      kit.add(box, wood(sx + 2), fm, place(sx * 0.62, 0.45, 0.22, 0, new THREE.Vector3(0.08, 0.9, 0.08), -0.45));
      for (let i = 0; i < 3; i++) kit.add(rock, ROCK[i % ROCK.length] as string, fm, place(sx * 0.62 + Math.cos(i * 2.1) * 0.16, 0.05, Math.sin(i * 2.1) * 0.16, i, 1.1));
    }
    kit.add(box, wood(0), fm, place(0, 2.12, 0, 0, new THREE.Vector3(1.5, 0.14, 0.16)));
    kit.add(new THREE.CylinderGeometry(0.16, 0.16, 0.08, 12).rotateZ(Math.PI / 2), IRON_DARK, fm, place(0, 1.98, 0));
    this.frameTop.set(0, 1.92, 0).applyMatrix4(fm);
    this.frameFoot.set(0, 0, 0).applyMatrix4(fm);
    // The chain back to the bridge: low posts along the second road with a dark chain strung over them.
    const back = road.slice(bridge + 1).map((p) => at(p));
    for (let i = 1; i < back.length; i++) {
      const a = back[i - 1] as THREE.Vector3;
      const b = back[i] as THREE.Vector3;
      const yaw = Math.atan2(b.x - a.x, b.z - a.z);
      const off = new THREE.Vector3(Math.cos(yaw), 0, -Math.sin(yaw)).multiplyScalar(0.48);
      const pa = a.clone().add(off);
      const pb = b.clone().add(off);
      const mid = pa.clone().lerp(pb, 0.5);
      const ya = ctx.ground(pa.x, pa.z) + 0.32;
      const yb = ctx.ground(pb.x, pb.z) + 0.32;
      kit.add(box, '#26272b', place(mid.x, (ya + yb) / 2 - 0.03, mid.z, yaw, new THREE.Vector3(0.025, 0.025, pa.distanceTo(pb))));
      kit.add(post, wood(i), place(pb.x, ctx.ground(pb.x, pb.z) + 0.17, pb.z, 0, new THREE.Vector3(0.6, 0.35, 0.6)));
    }

    const stationMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, metalness: 0.08, flatShading: true });
    const station = kit.build(stationMat, 'mine-station');
    this.mats.push(stationMat);
    if (station) {
      station.receiveShadow = true;
      station.castShadow = !ctx.low;
      this.geos.push(station.geometry);
      this.group.add(station);
    }
    const glassMat = new THREE.MeshBasicMaterial({ vertexColors: true });
    const lit = glass.build(glassMat, 'mine-lamps');
    this.mats.push(glassMat);
    if (lit) {
      this.geos.push(lit.geometry);
      this.group.add(lit);
    }

    // The chasm: a black gap across the road, red far down in it (its glow is drawn each frame).
    const pitGeo = new THREE.PlaneGeometry(2.4, this.span + 0.2).rotateX(-Math.PI / 2);
    const pitMat = new THREE.MeshBasicMaterial({ color: '#040303', polygonOffset: true, polygonOffsetFactor: -4, polygonOffsetUnits: -8 });
    const pit = new THREE.Mesh(pitGeo, pitMat);
    pit.position.copy(this.chasm).setY(this.chasm.y + 0.02);
    pit.rotation.y = Math.atan2(this.along.x, this.along.z);
    pit.renderOrder = 1;
    pit.updateMatrix();
    pit.matrixAutoUpdate = false;
    this.geos.push(pitGeo);
    this.mats.push(pitMat);
    this.group.add(pit);

    // The bridge leaf: planks across, its rails on it, iron straps; hinged at the far side (local origin), lying back toward -z.
    const lk = new Kit();
    const o = new THREE.Matrix4();
    const planks = Math.max(3, Math.round(this.span / 0.16));
    for (let i = 0; i < planks; i++) lk.add(box, wood(i), o, place(0, 0.03, -((i + 0.5) / planks) * (this.span + 0.1), ((i * 5) % 3 - 1) * 0.03, new THREE.Vector3(0.86, 0.05, (this.span + 0.1) / planks - 0.015)));
    for (const sx of [-1, 1]) {
      lk.add(box, IRON, o, place(sx * 0.2, 0.08, -(this.span + 0.1) / 2, 0, new THREE.Vector3(0.045, 0.05, this.span + 0.1)));
      lk.add(box, '#2a2b2f', o, place(sx * 0.42, 0.04, -(this.span + 0.1) / 2, 0, new THREE.Vector3(0.04, 0.06, this.span + 0.1)));
    }
    const leafMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, flatShading: true });
    this.leaf = lk.build(leafMat, 'mine-bridge') as THREE.Mesh;
    this.leaf.matrixAutoUpdate = false;
    this.leaf.castShadow = !ctx.low;
    this.mats.push(leafMat);
    this.geos.push(this.leaf.geometry);
    this.group.add(this.leaf);

    // The counterweight: a great block of dressed stone in iron straps, a ring on top for the chain.
    const wk = new Kit();
    wk.add(box, '#55545c', o, place(0, 0, 0, 0, new THREE.Vector3(0.62, 0.56, 0.62)));
    wk.add(box, '#45444b', o, place(0, 0.02, 0, 0.785, new THREE.Vector3(0.5, 0.5, 0.5)));
    for (const y of [-0.17, 0.17]) wk.add(box, IRON_DARK, o, place(0, y, 0, 0, new THREE.Vector3(0.66, 0.06, 0.66)));
    for (const sx of [-1, 1]) wk.add(box, IRON_DARK, o, place(sx * 0.32, 0, 0, 0, new THREE.Vector3(0.04, 0.6, 0.12)));
    wk.add(new THREE.TorusGeometry(0.09, 0.025, 4, 10), IRON, o, place(0, 0.36, 0));
    // A dwarven rune cut in its face, pale: the thing to break.
    wk.add(box, '#b9bcc6', o, place(0, 0.02, 0.315, 0, new THREE.Vector3(0.05, 0.26, 0.01)));
    wk.add(box, '#b9bcc6', o, place(0, 0.09, 0.315, 0.0, new THREE.Vector3(0.2, 0.04, 0.01)));
    wk.add(box, '#b9bcc6', o, place(0, -0.06, 0.315, 0.0, new THREE.Vector3(0.14, 0.04, 0.01)));
    const weightMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.9, flatShading: true });
    this.weight = wk.build(weightMat, 'mine-weight') as THREE.Mesh;
    this.weight.matrixAutoUpdate = false;
    this.weight.castShadow = !ctx.low;
    this.mats.push(weightMat);
    this.geos.push(this.weight.geometry);
    this.group.add(this.weight);
    this.weightY = this.frameTop.y - 0.5;
    const ck = new Kit();
    const link = new THREE.TorusGeometry(0.035, 0.012, 4, 8);
    for (let i = 0; i < 10; i++) ck.add(link, i % 2 ? '#3c3e44' : '#4a4d54', o, place(0, -(i + 0.5) / 10, 0, (i % 2) * Math.PI / 2, new THREE.Vector3(1, 1.4, 1)));
    link.dispose();
    const chainMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.5, metalness: 0.5, flatShading: true });
    this.chain = ck.build(chainMat, 'mine-chain') as THREE.Mesh;
    this.chain.matrixAutoUpdate = false;
    this.mats.push(chainMat);
    this.geos.push(this.chain.geometry);
    this.group.add(this.chain);

    // The train and the wrecks.
    const cg = cartGeo();
    this.geos.push(cg);
    const cartMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.75, metalness: 0.12, flatShading: true });
    this.mats.push(cartMat);
    this.carts = new THREE.InstancedMesh(cg, cartMat, MAX_CARTS);
    this.carts.count = 0;
    this.carts.castShadow = !ctx.low;
    this.carts.frustumCulled = false;
    this.wrecks = new THREE.InstancedMesh(cg, cartMat, MAX_CARTS);
    this.wrecks.count = 0;
    this.wrecks.frustumCulled = false;
    this.group.add(this.carts, this.wrecks, this.glow.mesh);

    // The rim: where the Marrow's red comes up from below, all round the cavern's far edge.
    const rimR = (map.radius - 1.5) * WORLD_PER_HEX;
    for (let i = 0; i < 14; i++) {
      const a = (i / 14) * Math.PI * 2;
      this.rim.push(new THREE.Vector3(Math.cos(a) * rimR, ctx.ground(Math.cos(a) * rimR, Math.sin(a) * rimR) - 0.3, Math.sin(a) * rimR));
    }
    for (const g of [box, post, rock]) g.dispose();
    this.group.updateMatrixWorld(true);
  }

  /** A lamp on a post: a timber post, an arm, an iron cage with warm glass (its glow is drawn each frame). */
  private lampPost(kit: Kit, glass: Kit, p: THREE.Vector3, yaw: number, i: number): void {
    const m = place(p.x, p.y, p.z, yaw);
    const box = new THREE.BoxGeometry(1, 1, 1);
    kit.add(box, WOOD[i % WOOD.length] as string, m, place(0, 0.6, 0, 0, new THREE.Vector3(0.09, 1.2, 0.09)));
    kit.add(box, WOOD[(i + 1) % WOOD.length] as string, m, place(0, 1.18, 0.14, 0, new THREE.Vector3(0.06, 0.06, 0.34)));
    for (let k = 0; k < 3; k++) kit.add(new THREE.DodecahedronGeometry(0.09, 0), ROCK[k % ROCK.length] as string, m, place(Math.cos(k * 2.1) * 0.12, 0.04, Math.sin(k * 2.1) * 0.12, k));
    const cage = place(0, 1.0, 0.28);
    const mm = m.clone().multiply(cage);
    kit.add(box, IRON_DARK, mm, place(0, 0.13, 0, 0, new THREE.Vector3(0.16, 0.03, 0.16)));
    kit.add(box, IRON_DARK, mm, place(0, -0.09, 0, 0, new THREE.Vector3(0.14, 0.03, 0.14)));
    for (const [x, z] of [[-1, -1], [1, -1], [-1, 1], [1, 1]] as const) kit.add(box, IRON_DARK, mm, place(x * 0.065, 0.02, z * 0.065, 0, new THREE.Vector3(0.015, 0.2, 0.015)));
    kit.add(box, IRON_DARK, mm, place(0, 0.19, 0, 0, new THREE.Vector3(0.012, 0.1, 0.012)));
    glass.add(new THREE.CylinderGeometry(0.05, 0.055, 0.15, 6), LAMP, mm, place(0, 0.02, 0));
    box.dispose();
    this.lamps.push(new THREE.Vector3(0, 0.02, 0).applyMatrix4(mm));
  }

  /** A timber frame over a road (two posts and a lintel, braced), a lamp hung under the lintel if `lamp`. */
  private timbers(kit: Kit, glass: Kit, p: THREE.Vector3, yaw: number, i: number, lamp: boolean): void {
    const m = place(p.x, this.ctx.ground(p.x, p.z), p.z, yaw);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const wood = (k: number): string => WOOD[((i + k) % WOOD.length + WOOD.length) % WOOD.length] as string;
    for (const sx of [-1, 1]) {
      kit.add(box, wood(sx), m, place(sx * 0.62, 0.62, 0, 0, new THREE.Vector3(0.1, 1.24, 0.1), 0, sx * 0.04));
      kit.add(box, wood(sx + 2), m, place(sx * 0.5, 1.1, 0, 0, new THREE.Vector3(0.06, 0.3, 0.06), 0, sx * 0.8));
    }
    kit.add(box, wood(1), m, place(0, 1.25, 0, 0, new THREE.Vector3(1.4, 0.12, 0.13)));
    if (lamp) {
      kit.add(box, IRON_DARK, m, place(0, 1.12, 0, 0, new THREE.Vector3(0.012, 0.16, 0.012)));
      kit.add(box, IRON_DARK, m, place(0, 0.99, 0, 0, new THREE.Vector3(0.13, 0.025, 0.13)));
      glass.add(new THREE.CylinderGeometry(0.045, 0.05, 0.12, 6), LAMP, m, place(0, 0.92, 0));
      this.lamps.push(new THREE.Vector3(0, 0.92, 0).applyMatrix4(m));
    }
    box.dispose();
  }

  /** Re-dresses the board as the cavern: slate floor, stone spires for the woods, a cool murk with red from below. Call once. */
  apply(scene: THREE.Scene, terrain: TerrainBuild, sky: AscuasSky | null, extra: THREE.Object3D[]): void {
    repaint(terrain.mesh.geometry, new THREE.Color('#1c1e23'), new THREE.Color('#5a5f6b'), 0.03, 0.22);
    const c = new THREE.Color();
    const spire = [new THREE.Color('#2b2a2f'), new THREE.Color('#4a4850'), new THREE.Color('#3a3236')];
    const rock = (o: THREE.Object3D): void => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      if (o.name === 'outskirts-ground') {
        repaint(mesh.geometry, new THREE.Color('#0e0e11'), new THREE.Color('#34343a'), 0.008, 0.11);
        return;
      }
      if (o.name === 'roads') {
        repaint(mesh.geometry, new THREE.Color('#1c1916'), new THREE.Color('#463d35'), 0.02, 0.14);
        return;
      }
      if (o.name === 'tufts') {
        o.visible = false;
        return;
      }
      const mat = mesh.material as THREE.MeshStandardMaterial;
      if (!mat?.isMeshStandardMaterial) return;
      // Lit things (the fog's crystals) stay: blue veins in the rock suit a mine.
      if (mat.emissive && mat.emissive.getHex() !== 0) return;
      const im = o as THREE.InstancedMesh;
      if (im.isInstancedMesh && im.instanceColor) {
        // The woods to stone spires, the bushes and boulders to slate.
        for (let i = 0; i < im.count; i++) {
          im.getColorAt(i, c);
          const l = clamp01(lum(c.r, c.g, c.b) / 0.12);
          c.copy(spire[i % 3] as THREE.Color).lerp(spire[1] as THREE.Color, l * 0.5);
          im.setColorAt(i, c);
        }
        im.instanceColor.needsUpdate = true;
        return;
      }
      if (mat.vertexColors || mat.map) return;
      const m2 = mat.clone();
      m2.color.lerp(spire[0] as THREE.Color, 0.7);
      mesh.material = m2;
      this.mats.push(m2);
    };
    terrain.decor.traverse(rock);
    for (const o of extra) o.traverse(rock);
    // Deep under the shell: near black overhead, a cool murk, the lamps' warmth for a key, the Marrow's red from below.
    scene.background = new THREE.Color('#07080b');
    if (scene.fog instanceof THREE.Fog) scene.fog.color.set('#0c0d11');
    terrain.fog?.tint({ low: '#2a120e', mid: '#111217', far: '#07080b', alpha: 1 });
    if (sky) {
      sky.hemi.color.set('#7a86a6');
      sky.hemi.groundColor.set('#3a120a');
      sky.hemi.intensity *= 0.7;
      sky.key.color.set('#e8ddd2');
      sky.key.intensity *= 0.7;
      sky.rim.color.set('#c8502e');
      sky.rim.intensity *= 0.6;
    }
  }

  /** Every fresh tick: the train's sparks and dust, a barricade rising, a cart off the rails, the bridge, the fall. */
  tick(s: BattleState): void {
    this.s = s;
    const low = this.ctx.low;
    const r = s.rules?.rails;
    // The Marrow breathes now and then: red motes rising past the rim.
    if (s.tick % (low ? 9 : 4) === 0) {
      const p = this.rim[(s.tick * 7) % this.rim.length] as THREE.Vector3;
      this.ctx.vfx.burst(p.clone().setY(p.y + 0.4), { n: 1, color: '#ff6a3a', speed: 0.01, up: 0.04, size: 0.06, life: 30, gravity: -0.0015, spread: 1.2 });
    }
    // Quick: sparks off the wheels of the last cart, more the faster it goes.
    const sp = s.escort?.speed ?? 0;
    if (r && this.shown > 0 && sp > r.speed[0] + (r.speed[1] - r.speed[0]) * 0.45 && this.fellAt < 0 && s.tick % (low ? 3 : 1) === 0) {
      const p = this.cartAt(this.shown - 1, this.v);
      if (p) this.ctx.vfx.burst(p.clone().setY(p.y + 0.08), { n: low ? 1 : 2, color: '#ffc27a', speed: 0.035, up: 0.02, size: 0.035, life: 7, gravity: 0.006, spread: 0.25 });
    }
    for (const e of s._events ?? []) {
      if (e.t !== 'rails') continue;
      const p = this.ctx.at(e.x, e.y, 0.2);
      if (e.kind === 'wall') {
        // Bones heave up out of the rail bed.
        this.ctx.vfx.burst(p, { n: low ? 4 : 9, color: '#4a4448', speed: 0.025, up: 0.035, size: 0.3, life: 18, smoke: true, spread: 0.5 });
        this.ctx.vfx.ring(p.clone().setY(p.y - 0.15), 0.2, 1.3, '#a98bff', 10, 0.5);
      } else if (e.kind === 'derail') {
        this.derail(p);
      } else if (e.kind === 'up' || e.kind === 'down') {
        // The winch: chain rattling, dust off the hinge and the posts.
        this.ctx.vfx.burst(this.hinge.clone().setY(this.hinge.y + 0.3), { n: low ? 4 : 10, color: '#5a5450', speed: 0.02, up: 0.025, size: 0.3, life: 22, smoke: true, spread: 0.6 });
        if (e.kind === 'down') {
          // The counterweight broken: stone chunks and dust at the frame, the bridge banging down.
          const f = this.frameFoot.clone().setY(this.frameFoot.y + 0.4);
          this.ctx.vfx.burst(f, { n: low ? 8 : 18, color: '#55545c', speed: 0.06, up: 0.06, size: 0.09, life: 20, gravity: 0.01, spread: 0.5 });
          this.ctx.vfx.burst(f, { n: low ? 4 : 9, color: '#4a4448', speed: 0.025, up: 0.025, size: 0.45, life: 26, smoke: true, spread: 0.6 });
          this.ctx.flare(f, '#e8e4ff', 2, 10);
          this.ctx.shake(0.2);
        } else this.ctx.vfx.ring(this.frameFoot.clone().setY(this.frameFoot.y + 0.05), 0.3, 2.2, '#ff8a6e', 14, 0.6);
      } else if (e.kind === 'fall') {
        this.fellAt = this.now;
        this.fellMats = [];
        for (let i = 0; i < this.carts.count; i++) {
          const mm = new THREE.Matrix4();
          this.carts.getMatrixAt(i, mm);
          this.fellMats.push(mm);
        }
        this.ctx.shake(0.35);
        this.ctx.vfx.burst(this.chasm.clone().setY(this.chasm.y + 0.3), { n: low ? 8 : 20, color: '#5a4130', speed: 0.06, up: 0.05, size: 0.08, life: 22, gravity: 0.01, spread: 0.6 });
      }
    }
  }

  /** A cart off the rails: it rolls off to one side and lies there in pieces; splinters, sparks, dust. */
  private derail(p: THREE.Vector3): void {
    const low = this.ctx.low;
    const yaw = this.yawAt(p.x, p.z);
    const side = new THREE.Vector3(Math.cos(yaw), 0, -Math.sin(yaw)).multiplyScalar(this.wrecked.length % 2 ? -1 : 1);
    if (this.wrecked.length < MAX_CARTS) this.wrecked.push({ at: p.clone().setY(this.ctx.ground(p.x, p.z)), side, yaw, t0: this.now });
    this.ctx.flare(p.clone().setY(p.y + 0.3), '#ffd6b0', 2.2, 8);
    this.ctx.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 8 : 20, color: '#6b4c36', speed: 0.07, up: 0.07, size: 0.07, life: 22, gravity: 0.01, spread: 0.5 });
    this.ctx.vfx.burst(p.clone().setY(p.y + 0.15), { n: low ? 5 : 12, color: '#ffc27a', speed: 0.06, up: 0.04, size: 0.035, life: 10, gravity: 0.008, spread: 0.3 });
    this.ctx.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 4 : 9, color: '#4a4448', speed: 0.025, up: 0.025, size: 0.4, life: 26, smoke: true, spread: 0.6 });
    this.ctx.shake(0.25);
  }

  /** Which way the rail runs at a world point (the nearest segment's), for a barricade lying across it. */
  private yawAt(x: number, z: number): number {
    let best = 0;
    let bd = Infinity;
    for (const sg of this.segs) {
      const mx = (sg.a.x + sg.b.x) / 2 - x;
      const mz = (sg.a.z + sg.b.z) / 2 - z;
      const d = mx * mx + mz * mz;
      if (d < bd) {
        bd = d;
        best = Math.atan2(sg.b.x - sg.a.x, sg.b.z - sg.a.z);
      }
    }
    return best;
  }

  /** A barricade's yaw (lying across the rail), kept per nightmare id. */
  wallYaw(id: number, x: number, z: number): number {
    let y = this.yawCache.get(id);
    if (y === undefined) {
      // The bone wall is built along its x: turned a quarter, it lies across the rail.
      y = this.yawAt(x, z) + Math.PI / 2;
      this.yawCache.set(id, y);
    }
    return y;
  }

  /** The `i`-th cart's ground point as drawn (the one behind the head `i` gaps back along the rail), or null. */
  private cartAt(i: number, out: THREE.Vector3): THREE.Vector3 | null {
    const p = this.pts[i];
    if (!p) return null;
    return out.copy(this.ctx.at(p.x, p.y, 0));
  }

  /**
   * Every redraw: the train, its head at sim (x, y) heading for rail point `next` (the cart bot as drawn), `left` carts
   * of it; the bridge (up while the counterweight hangs low), the counterweight, the wrecks, the glows.
   */
  draw(s: BattleState, x: number, y: number, next: number, left: number): void {
    const st = s.rails;
    // The carts back along the rail from the head, one gap apart.
    const n = Math.max(0, Math.min(MAX_CARTS, left));
    this.pts.length = 0;
    // Each cart's front and back wheels: points GAP/2 either side of its middle.
    const along: Pt[] = [];
    {
      let px = x;
      let py = y;
      let idx = next - 1;
      const want = (n - 1) * GAP + GAP / 2 + 1;
      along.push({ x, y });
      let walked = 0;
      // Every GAP/2 a point, walking back.
      const step = GAP / 2;
      let need = step;
      while (walked < want && idx >= 0) {
        const t = this.road[idx] as Pt;
        const dx = t.x - px;
        const dy = t.y - py;
        const d = Math.hypot(dx, dy);
        if (d >= need) {
          px += (dx / d) * need;
          py += (dy / d) * need;
          walked += need;
          along.push({ x: px, y: py });
          need = step;
        } else {
          need -= d;
          walked += d;
          px = t.x;
          py = t.y;
          idx--;
        }
      }
    }
    let shown = 0;
    if (this.fellAt < 0) {
      for (let i = 0; i < n; i++) {
        const front = along[i * 2];
        const mid = along[i * 2 + 1];
        const rear = along[i * 2 + 2] ?? mid;
        if (!front || !mid || !rear) break;
        this.pts[i] = mid;
        const f = this.ctx.at(front.x, front.y, 0);
        const b = this.ctx.at(rear.x, rear.y, 0);
        const p = this.ctx.at(mid.x, mid.y, 0);
        p.y = Math.max(f.y, b.y, this.ctx.ground(p.x, p.z)) + 0.1;
        const yaw = Math.atan2(f.x - b.x, f.z - b.z);
        const pitch = Math.atan2(b.y - f.y, Math.hypot(f.x - b.x, f.z - b.z));
        // A rattle on the rail joints.
        const rattle = Math.sin(this.now * 37 + i * 1.7) * 0.012;
        this.q.setFromEuler(this.e.set(pitch, yaw, rattle, 'YXZ'));
        this.m.compose(this.v.set(p.x, p.y + Math.abs(rattle) * 0.6, p.z), this.q, this.one);
        this.carts.setMatrixAt(shown++, this.m);
        if (i === 0) {
          this.head.copy(p);
          this.headYaw = yaw;
        }
      }
      this.carts.count = shown;
      this.shown = shown;
    } else {
      // Over the edge: they tip forward into the chasm and drop out of sight.
      const t = this.now - this.fellAt;
      for (let i = 0; i < this.fellMats.length; i++) {
        const k = Math.max(0, t - i * 0.12);
        this.m.copy(this.fellMats[i] as THREE.Matrix4);
        const pos = this.v.setFromMatrixPosition(this.m);
        pos.addScaledVector(this.along, Math.min(k, 0.4) * 1.6);
        pos.y -= k * k * 6;
        this.q.setFromRotationMatrix(this.m);
        this.q.multiply(new THREE.Quaternion().setFromEuler(this.e.set(Math.min(1.2, k * 2.2), 0, 0)));
        this.m.compose(pos, this.q, this.one);
        this.carts.setMatrixAt(i, this.m);
      }
      this.carts.count = this.fellMats.length;
      this.shown = 0;
    }
    this.carts.instanceMatrix.needsUpdate = true;

    // The wrecks: rolling off, then lying on their side.
    this.wrecked.forEach((w, i) => {
      const k = Math.min(1, (this.now - w.t0) / 0.9);
      const arc = Math.sin(k * Math.PI) * 0.5;
      const p = this.v.copy(w.at).addScaledVector(w.side, 0.4 + k * 0.7);
      p.y += 0.12 + arc;
      const roll = k * 1.9 + (1 - k) * k * 1.5;
      this.q.setFromEuler(this.e.set(0.2 * k, w.yaw + k * 0.9, Math.sign(w.side.x + w.side.z + 1e-6) * roll, 'YXZ'));
      this.m.compose(p, this.q, this.one);
      this.wrecks.setMatrixAt(i, this.m);
    });
    this.wrecks.count = this.wrecked.length;
    this.wrecks.instanceMatrix.needsUpdate = true;

    // The bridge: up while the counterweight hangs low, down once it is broken (or before it ever came down).
    const up = !!st?.weight && !st.down;
    const dt = Math.min(0.1, Math.max(0, this.now - this.last));
    this.last = this.now;
    const target = up ? LEAF_UP : 0;
    this.leafAngle += (target - this.leafAngle) * Math.min(1, dt * (up ? 2.2 : 5));
    const ly = this.ctx.ground(this.hinge.x, this.hinge.z) + 0.07;
    this.q.setFromEuler(this.e.set(this.leafAngle, Math.atan2(this.along.x, this.along.z), 0, 'YXZ'));
    this.leaf.matrix.compose(this.w.set(this.hinge.x, ly, this.hinge.z), this.q, this.one);
    this.leaf.matrixWorldNeedsUpdate = true;
    // The counterweight: high under the beam, dropped low when it pulls the bridge up, gone when broken.
    const broken = !!st?.down;
    this.weight.visible = !broken;
    this.chain.visible = !broken;
    if (!broken) {
      const wantY = up ? this.frameFoot.y + 0.45 : this.frameTop.y - 0.5;
      this.weightY += (wantY - this.weightY) * Math.min(1, dt * 3);
      const sway = up ? Math.sin(this.now * 1.7) * 0.05 : 0;
      this.q.setFromEuler(this.e.set(sway, 0, sway * 0.6));
      this.weight.matrix.compose(this.w.set(this.frameFoot.x, this.weightY, this.frameFoot.z), this.q, this.one);
      this.weight.matrixWorldNeedsUpdate = true;
      // The chain from the pulley to the ring on its top.
      const len = Math.max(0.05, this.frameTop.y - (this.weightY + 0.36));
      this.q.setFromEuler(this.e.set(sway * 0.5, 0, sway * 0.3));
      this.chain.matrix.compose(this.w.set(this.frameTop.x, this.frameTop.y, this.frameTop.z), this.q, this.v.set(1, len, 1));
      this.chain.matrixWorldNeedsUpdate = true;
    }

    // Glows: the lamps, the train's lantern, the chasm and the rim.
    this.glow.begin();
    for (let i = 0; i < this.lamps.length; i++) {
      const p = this.lamps[i] as THREE.Vector3;
      const flick = 0.85 + 0.15 * Math.sin(this.now * 9 + i * 2.3) * Math.sin(this.now * 5.7 + i);
      this.glow.add(p.x, p.y, p.z, LAMP_CORE, 0.35, 0.8 * flick);
      this.glow.add(p.x, p.y, p.z, LAMP, 1.5, 0.32 * flick);
      this.glow.add(p.x, this.ctx.ground(p.x, p.z) + 0.05, p.z, LAMP, 2.4, 0.07 * flick);
    }
    if (this.shown > 0 && this.fellAt < 0) {
      const fx = Math.sin(this.headYaw);
      const fz = Math.cos(this.headYaw);
      const flick = 0.85 + 0.15 * Math.sin(this.now * 13);
      this.glow.add(this.head.x + fx * 0.36, this.head.y + 0.55, this.head.z + fz * 0.36, LAMP_CORE, 0.3, 0.9 * flick);
      this.glow.add(this.head.x + fx * 0.36, this.head.y + 0.55, this.head.z + fz * 0.36, LAMP, 1.3, 0.4 * flick);
      // Its light thrown ahead down the rail.
      this.glow.add(this.head.x + fx * 1.6, this.head.y + 0.05, this.head.z + fz * 1.6, LAMP, 2.6, 0.1 * flick);
    }
    const beat = 0.85 + 0.15 * Math.sin(this.now * 1.3);
    this.glow.add(this.chasm.x, this.chasm.y - 0.1, this.chasm.z, MARROW, 1.9, 0.38 * beat);
    for (let i = 0; i < this.rim.length; i++) {
      const p = this.rim[i] as THREE.Vector3;
      this.glow.add(p.x, p.y, p.z, MARROW, 6, 0.055 * (0.8 + 0.2 * Math.sin(this.now * 0.7 + i)));
    }
    if (up && !broken) this.glow.add(this.frameFoot.x, this.weightY + 0.1, this.frameFoot.z, SPARK, 1.4, 0.16 + 0.08 * Math.sin(this.now * 4));
    this.glow.end();
  }

  /** Where the counterweight hangs as drawn (for its life bar), and its top. */
  weightAt(out: THREE.Vector3): THREE.Vector3 {
    return out.set(this.frameFoot.x, this.weightY + 0.45, this.frameFoot.z);
  }

  frame(now: number): void {
    this.now = now;
  }

  dispose(): void {
    for (const g of this.geos) g.dispose();
    for (const m of this.mats) m.dispose();
    this.carts.dispose();
    this.wrecks.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
  }
}
